import { useCallback, useEffect, useRef, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissionCheck } from './usePermissionCheck';
import { supabase } from '../lib/supabase';
import { getBookingHelperIssues, type BookingHelperIssue } from '../lib/bookingHelper';

/**
 * Current Bookings Helper: when enabled for the business, admins are shown a
 * modal on app load listing bookings whose status is out of sync with today
 * (stuck in progress / overdue scheduled). Detection runs server-side.
 */
export function useBookingHelper() {
  const { selectedBusiness } = useBusiness();
  const { canViewAdminDashboard, isLoading: permissionsLoading } = usePermissionCheck();

  const [isOpen, setIsOpen] = useState(false);
  const [issues, setIssues] = useState<BookingHelperIssue[]>([]);

  // Only auto-open once per business load (per session). Closing won't re-pop
  // until the next app load / business switch.
  const checkedForBusiness = useRef<string | null>(null);

  const businessId = selectedBusiness?.id ?? null;

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const debug = import.meta.env.DEV;
    if (permissionsLoading || !businessId || !canViewAdminDashboard) {
      if (debug) {
        console.debug('[BookingHelper] gate not passed', {
          permissionsLoading,
          businessId,
          canViewAdminDashboard,
        });
      }
      return;
    }
    if (checkedForBusiness.current === businessId) return;

    // NOTE: do not set the "checked" guard up-front. Under React StrictMode the
    // effect mounts, cleans up, then re-mounts; setting the guard early would let
    // the (cancelled) first run block the live second run. Mark it checked only
    // once we actually act on a live run.
    let active = true;

    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_app_setting', {
          p_business_id: businessId,
          p_app_slug: 'calendar',
          p_setting_key: 'current_bookings_helper_enabled',
          p_default_value: false,
        });
        if (debug) {
          console.debug('[BookingHelper] setting read', { data, error, active });
        }
        if (!active || error || data !== true) return;

        const found = await getBookingHelperIssues(businessId);
        if (debug) {
          console.debug('[BookingHelper] issues found', found.length, { active });
        }
        if (!active) return;

        checkedForBusiness.current = businessId;
        if (found.length > 0) {
          setIssues(found);
          setIsOpen(true);
        }
      } catch (err) {
        console.error('Booking helper check failed:', err);
      }
    })();

    return () => {
      active = false;
    };
  }, [businessId, canViewAdminDashboard, permissionsLoading]);

  return { isOpen, issues, close };
}
